import { useCallback, useEffect, useRef, useState } from "react";
import { FileSpreadsheet, ListPlus, Plus, Save, Sparkles, Trash2 } from "lucide-react";
import { useToast } from "@/shared/providers/ToastProvider";
import { inputClass } from "./FormField";
import {
  extractEntryLines,
  fetchEntryLines,
  parseEntryLinesCsv,
  saveEntryLines,
  type EntryLine,
  type EntryLinesResponse,
  type EntryScope,
} from "../api/invoiceEntryApi";

type Props = {
  invoiceId: number;
  /** auditor : l'auditeur lance le traitement ; admin : saisie de secours. */
  scope?: EntryScope;
  /** Appelé après l'enregistrement des lignes (rechargement de la codification). */
  onSaved?: () => void;
};

type Proposal = { source: "csv" | "ia"; lines: EntryLine[]; notes: string[] };

const NEW_LINE: EntryLine = { date: "", patient_name: "", patient_ref: "", label: "", quantity: 1, amount: 0, diagnosis: "" };

function money(value: number): string {
  return value.toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

/**
 * Facture détaillée, une ligne par acte (date, patient tel qu'écrit, matricule, libellé, quantité, montant,
 * diagnostic écrit). L'auditeur lance le traitement : lecture automatique du PDF joint par Claude ; en secours,
 * saisie à la main ou import d'un CSV. Les lignes proposées sont vérifiées avant d'être enregistrées.
 */
export function InvoiceEntryLines({ invoiceId, scope = "admin", onSaved }: Props) {
  const isAudit = scope === "auditor";
  const { showToast } = useToast();
  const [data, setData] = useState<EntryLinesResponse | null>(null);
  const [draft, setDraft] = useState<EntryLine[]>([]);
  const [proposal, setProposal] = useState<Proposal | null>(null);
  const [dirty, setDirty] = useState(false);
  const [busy, setBusy] = useState<null | "save" | "csv" | "ia">(null);
  const [error, setError] = useState<string | null>(null);
  const csvInput = useRef<HTMLInputElement>(null);

  const apply = (response: EntryLinesResponse) => {
    setData(response);
    setDraft(response.lines.map((line) => ({ ...line })));
    setDirty(false);
  };

  const load = useCallback(() => {
    fetchEntryLines(invoiceId, scope)
      .then(apply)
      .catch((err) => setError(err instanceof Error ? err.message : "Chargement impossible."));
  }, [invoiceId, scope]);

  useEffect(load, [load]);

  const editable = data?.editable ?? false;
  const linesTotal = draft.reduce((sum, line) => sum + (Number(line.amount) || 0), 0);
  const declared = data?.totals.declared ?? null;
  const difference = declared !== null ? Math.round((declared - linesTotal) * 100) / 100 : null;
  const currency = data?.totals.currency ?? "USD";

  const update = (index: number, patch: Partial<EntryLine>) => {
    setDraft((lines) => lines.map((line, i) => (i === index ? { ...line, ...patch } : line)));
    setDirty(true);
  };

  const handleSave = async () => {
    setBusy("save");
    setError(null);
    try {
      apply(await saveEntryLines(invoiceId, draft, scope));
      showToast("Lignes de la facture enregistrées.");
      onSaved?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Enregistrement impossible.");
    } finally {
      setBusy(null);
    }
  };

  const handleCsv = async (file: File | undefined) => {
    if (!file) return;
    setBusy("csv");
    setError(null);
    try {
      const result = await parseEntryLinesCsv(invoiceId, file, scope);
      setProposal({ source: "csv", lines: result.lines, notes: result.errors });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Import impossible.");
    } finally {
      setBusy(null);
      if (csvInput.current) csvInput.current.value = "";
    }
  };

  const handleExtract = async () => {
    setBusy("ia");
    setError(null);
    try {
      const result = await extractEntryLines(invoiceId, scope);
      const notes = [result.notes].filter(Boolean);
      const sum = result.lines.reduce((s, l) => s + l.amount, 0);
      if (result.total_detected > 0 && Math.abs(result.total_detected - sum) > 0.01) {
        notes.push(`Total imprimé sur la facture : ${money(result.total_detected)} ; somme des lignes lues : ${money(sum)}.`);
      }
      setProposal({ source: "ia", lines: result.lines, notes });
    } catch (err) {
      setError(err instanceof Error ? err.message : "Lecture automatique impossible.");
    } finally {
      setBusy(null);
    }
  };

  const acceptProposal = (mode: "replace" | "append") => {
    if (!proposal) return;
    const incoming = proposal.lines.map((l) => ({ ...l }));
    setDraft((lines) => (mode === "replace" ? incoming : [...lines, ...incoming]));
    setDirty(true);
    setProposal(null);
    showToast("Lignes ajoutées au tableau : vérifiez-les puis enregistrez.");
  };

  if (!data) {
    return (
      <section className="theme-card p-5">
        <p className="text-sm text-muted">{error ?? "Chargement des lignes de la facture…"}</p>
      </section>
    );
  }

  return (
    <section className="theme-card flex flex-col gap-4 p-5">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="flex items-center gap-2 text-base font-semibold text-foreground">
            <ListPlus size={18} className="text-primary" />
            Lignes de la facture détaillée ({draft.length})
          </h2>
          <p className="mt-1 max-w-3xl text-xs text-muted">
            {isAudit
              ? "Lancez le traitement : l'IA lit la facture jointe et propose une ligne par acte, avec le diagnostic écrit. Vérifiez puis enregistrez ; les actes sont alors regroupés en fréquentations. En secours, saisissez les lignes ou importez un CSV."
              : "Facultatif : l'auditeur lance le traitement de la facture. Saisie de secours : une ligne par acte, patient par patient, comme sur la facture."}{" "}
            Le patient est rattaché automatiquement au bénéficiaire quand le nom ou le matricule correspond.
            {editable ? "" : " La facture a été transmise au Sinistre : les lignes ne sont plus modifiables."}
          </p>
        </div>
        {editable ? (
          <div className="flex flex-wrap gap-2">
            <button
              type="button"
              disabled={busy !== null || !data.extraction_available}
              onClick={() => void handleExtract()}
              title={
                data.extraction_available
                  ? "Claude lit le PDF de la facture détaillée et propose les lignes"
                  : data.extraction_configured
                    ? "Aucune facture détaillée (PDF ou image) jointe"
                    : "Lecture automatique non configurée sur le serveur"
              }
              className="inline-flex items-center gap-2 rounded-lg border border-primary px-3 py-1.5 text-sm text-primary hover:bg-primary/5 disabled:opacity-50"
            >
              <Sparkles size={15} />
              {busy === "ia" ? "Lecture en cours (jusqu'à 2 min)…" : isAudit ? "Lancer le traitement (IA)" : "Lire la facture avec l'IA"}
            </button>
            <button
              type="button"
              disabled={busy !== null}
              onClick={() => csvInput.current?.click()}
              className="inline-flex items-center gap-2 rounded-lg border border-border px-3 py-1.5 text-sm text-foreground hover:bg-accent/20 hover:border-accent disabled:opacity-50"
            >
              <FileSpreadsheet size={15} />
              {busy === "csv" ? "Import…" : "Importer un CSV"}
            </button>
            <input ref={csvInput} type="file" accept=".csv,.txt" className="hidden" onChange={(e) => void handleCsv(e.target.files?.[0])} />
          </div>
        ) : null}
      </div>

      {editable ? (
        <p className="text-xs text-muted">
          CSV (Excel : Enregistrer sous → CSV) avec l'en-tête : date ; patient ; matricule ; acte ; quantite ; prix_unitaire ; montant.
        </p>
      ) : null}
      {error ? <p className="text-sm text-red-600">{error}</p> : null}

      {proposal ? (
        <div className="rounded-lg border border-primary/40 bg-primary/5 p-3 text-sm">
          <p className="font-medium text-foreground">
            {proposal.source === "ia" ? "Lecture automatique" : "Import CSV"} : {proposal.lines.length} ligne(s) proposée(s),
            total {money(proposal.lines.reduce((s, l) => s + l.amount, 0))} {currency}.
          </p>
          {proposal.notes.length > 0 ? (
            <ul className="mt-1 list-disc pl-5 text-xs text-amber-700">
              {proposal.notes.slice(0, 20).map((note) => (
                <li key={note}>{note}</li>
              ))}
            </ul>
          ) : null}
          {proposal.source === "ia" ? (
            <p className="mt-1 text-xs text-muted">Lecture faite par une IA : vérifiez chaque ligne avec la facture avant d'enregistrer.</p>
          ) : null}
          <div className="mt-2 flex flex-wrap gap-2">
            <button type="button" onClick={() => acceptProposal("replace")} className="rounded-lg bg-primary px-3 py-1.5 text-xs font-medium text-white hover:bg-accent hover:text-accent-foreground">
              Remplacer les lignes du tableau
            </button>
            {draft.length > 0 ? (
              <button type="button" onClick={() => acceptProposal("append")} className="rounded-lg border border-border px-3 py-1.5 text-xs text-foreground hover:bg-accent/20 hover:border-accent">
                Ajouter à la suite
              </button>
            ) : null}
            <button type="button" onClick={() => setProposal(null)} className="rounded-lg px-3 py-1.5 text-xs text-muted hover:text-foreground">
              Ignorer
            </button>
          </div>
        </div>
      ) : null}

      <div className="overflow-x-auto rounded-lg border border-border">
        <table className="w-full min-w-[860px] text-sm">
          <thead className="bg-card-muted text-left text-xs uppercase text-muted">
            <tr>
              <th className="px-2 py-2">Date</th>
              <th className="px-2 py-2">Patient (sur la facture)</th>
              <th className="px-2 py-2">Matricule</th>
              <th className="px-2 py-2">Acte</th>
              <th className="px-2 py-2 text-right">Qté</th>
              <th className="px-2 py-2 text-right">Montant</th>
              <th className="px-2 py-2">Diagnostic écrit</th>
              <th className="px-2 py-2">Bénéficiaire</th>
              {editable ? <th className="px-2 py-2" /> : null}
            </tr>
          </thead>
          <tbody>
            {draft.length === 0 ? (
              <tr>
                <td colSpan={9} className="px-3 py-5 text-center text-muted">
                  Aucune ligne. Lisez la facture avec l'IA, importez un CSV ou ajoutez les lignes à la main.
                </td>
              </tr>
            ) : null}
            {draft.map((line, index) => (
              <tr key={line.id ?? `new-${index}`} className="border-t border-border">
                <td className="px-2 py-1">
                  <input type="date" disabled={!editable} value={line.date} onChange={(e) => update(index, { date: e.target.value })} className={`${inputClass} h-8 w-36`} />
                </td>
                <td className="px-2 py-1">
                  <input disabled={!editable} value={line.patient_name} onChange={(e) => update(index, { patient_name: e.target.value })} className={`${inputClass} h-8`} />
                </td>
                <td className="px-2 py-1">
                  <input disabled={!editable} value={line.patient_ref} onChange={(e) => update(index, { patient_ref: e.target.value })} className={`${inputClass} h-8 w-28`} />
                </td>
                <td className="px-2 py-1">
                  <input disabled={!editable} value={line.label} onChange={(e) => update(index, { label: e.target.value })} className={`${inputClass} h-8`} />
                </td>
                <td className="px-2 py-1">
                  <input type="number" min={1} disabled={!editable} value={line.quantity} onChange={(e) => update(index, { quantity: Number(e.target.value) })} className={`${inputClass} h-8 w-16 text-right`} />
                </td>
                <td className="px-2 py-1">
                  <input type="number" min={0} step="0.01" disabled={!editable} value={line.amount} onChange={(e) => update(index, { amount: Number(e.target.value) })} className={`${inputClass} h-8 w-28 text-right`} />
                </td>
                <td className="px-2 py-1">
                  <input
                    disabled={!editable}
                    value={line.diagnosis ?? ""}
                    onChange={(e) => update(index, { diagnosis: e.target.value })}
                    placeholder="—"
                    aria-label="Diagnostic écrit sur la facture"
                    className={`${inputClass} h-8 w-36`}
                  />
                </td>
                <td className="px-2 py-1 text-xs">
                  {line.beneficiary ? <span className="text-foreground">{line.beneficiary}</span> : <span className="text-muted">{line.id ? "À rattacher (Audit)" : "—"}</span>}
                  {line.coded ? <span className="ml-1 rounded bg-green-100 px-1 text-[10px] text-green-800">codée</span> : null}
                </td>
                {editable ? (
                  <td className="px-2 py-1 text-right">
                    <button
                      type="button"
                      aria-label="Supprimer la ligne"
                      onClick={() => {
                        setDraft((lines) => lines.filter((_, i) => i !== index));
                        setDirty(true);
                      }}
                      className="rounded p-1 text-muted hover:bg-card-muted hover:text-red-600"
                    >
                      <Trash2 size={14} />
                    </button>
                  </td>
                ) : null}
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t-2 border-border bg-card-muted/40 text-sm">
              <td colSpan={5} className="px-2 py-2 text-right text-muted">Somme des lignes</td>
              <td className="px-2 py-2 text-right font-semibold tabular-nums text-foreground">{money(linesTotal)}</td>
              <td colSpan={editable ? 3 : 2} className="px-2 py-2 text-xs">
                {declared !== null ? (
                  <span className={difference !== null && Math.abs(difference) > 0.01 ? "text-amber-700" : "text-green-700"}>
                    Total déclaré {money(declared)} {currency}
                    {difference !== null && Math.abs(difference) > 0.01 ? ` · écart ${money(difference)}` : " · concordant"}
                  </span>
                ) : (
                  <span className="text-muted">Total déclaré non renseigné</span>
                )}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {editable ? (
        <div className="flex flex-wrap items-center justify-between gap-2">
          <button
            type="button"
            onClick={() => {
              setDraft((lines) => {
                const last = lines[lines.length - 1];
                return [...lines, { ...NEW_LINE, date: last?.date ?? "", patient_name: last?.patient_name ?? "", patient_ref: last?.patient_ref ?? "" }];
              });
              setDirty(true);
            }}
            className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
          >
            <Plus size={14} />
            Ajouter une ligne
          </button>
          <button
            type="button"
            disabled={busy !== null || !dirty}
            onClick={() => void handleSave()}
            className="inline-flex items-center gap-2 rounded-lg bg-primary px-4 py-2 text-sm font-medium text-white hover:bg-accent hover:text-accent-foreground disabled:opacity-60"
          >
            <Save size={15} />
            {busy === "save" ? "Enregistrement…" : "Enregistrer les lignes"}
          </button>
        </div>
      ) : null}
    </section>
  );
}
